import React, { useEffect } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CustomTextField from "../../../../../../components/widgets/CustomTextField";
import { designDetails } from "./FieldsData/DesignTabFields";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import { Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { CustomSelect } from "../../../../../../components/widgets/CustomSelect";
import Checkbox from "@mui/material/Checkbox";
import {
  GridStyle,
  designTabBoxProps,
  greyedOutFields,
} from "../../../../../../styles/muiProps";

const DesignDetailsGrid = () => {
  const {
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useFormContext({
    defaultValues: {
      CheckBox1: false,
      designCode: "",
      pwht: "",
      radiography: "",
    },
  });
  const selectValue = watch("designCode"); // Watch the 'select1' field
  console.log({ selectValue });

  useEffect(() => {
    if (selectValue && selectValue.includes("ASME VII DIV")) {
      setValue("editionAddenda", "Edition 2021");
    }
  }, [selectValue]);

  const isEnglish = watch("engUnits");
  const isSI = watch("siUnits");

  const shellRows = watch("DataGridView5") || [];
  const nozzleRows = watch("DataGridView1") || [];

  // Check whether Shell already contains real modeling data
  const hasShellModeling = shellRows.some((row) => {
    if (!row) return false;

    return Boolean(
      String(row.Component || "").trim() ||
        String(row.Diameter || "").trim() ||
        String(row["Can Length"] || "").trim() ||
        String(row["Provided Thk"] || "").trim()
    );
  });

  // Check whether Nozzles already contains real modeling data
  const hasNozzleModeling = nozzleRows.some((row) => {
    if (!row) return false;

    return Boolean(
      String(row.Mark || "").trim() ||
        String(row.Size || "").trim() ||
        String(row.Type || "").trim() ||
        String(row.Service || "").trim()
    );
  });

  // Once Shell OR Nozzle contains modeling, the project unit system cannot be changed anymore.
  const unitsLocked = hasShellModeling || hasNozzleModeling;

  const roundValue = (value, decimals = 4) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return value;
    }

    return Number(number.toFixed(decimals));
  };

  const convertField = (fieldName, converter) => {
    const value = getValues(fieldName);

    if (value === "" || value === null || value === undefined) {
      return;
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return;
    }

    setValue(fieldName, roundValue(converter(numericValue)), {
      shouldDirty: true,
    });
  };

  const nozzleLengthFields = [
    "Nozzle Weld (A)",
    "Inside Weld (B)",
    "Pad Weld (C)",
    "Outside Proj",
    "Inside Proj",
    "Pad OD",
    "Pad Thk",
  ];
  const saddleStressFields = [
    "txt_AS_SC",
    "saddleS1AtSaddle",
    "saddleS1Midspan",
    "saddleS2Away",
    "saddleS2Close",
    "saddleS3",
    "saddleS4Unstiffened",
    "saddleS4SmallL",
    "saddleS5",
  ];
  const saddleLengthFields = [
    "TxtBx_A_1",
    "TxtBx_DisBtwnSdls",
    "TxtBx_VOD",
    "TxtBx_Wb_CntrLin",
    "TxtBx_Wb_Thck",

    // Saddle calculation fields
    "txt_R_SC",
    "txt_LS_SC",
    "txt_LH_SC",
    "txt_SD_SC",
    "txt_TS_SC",
    "txt_TH_SC",
  ];

  const convertSIToEnglish = () => {
    // Pressure: N/mm² -> psi
    convertField("designInternalPressure", (v) => v * 145.0377377);
    convertField("designExternalPressure", (v) => v * 145.0377377);
    convertField("hydrotestPressure", (v) => v * 145.0377377);

    // Temperature: °C -> °F
    convertField("designTemperature", (v) => (v * 9) / 5 + 32);
    convertField("mdmt", (v) => (v * 9) / 5 + 32);

    // Length: mm -> in
    convertField("corrosionAllowanceShell", (v) => v / 25.4);
    convertField("vesselDiameter", (v) => v / 25.4);
    convertField("vesselLengthTT", (v) => v / 25.4);
    convertField("vesselLengthSS", (v) => v / 25.4);
    convertField("insulationThk", (v) => v / 25.4);

    // Height: m -> ft
    convertField("liquidHeight", (v) => v * 3.280839895);

    // Density: kg/m³ -> lb/ft³
    convertField("liquidDensity", (v) => v * 0.0624279606);
    convertField("insulationDensity", (v) => v * 0.0624279606);

    // Wind speed: km/h -> mph
    convertField("windSpeed", (v) => v * 0.6213711922);

    // SHELL - DataGridView5
    const shellRows = getValues("DataGridView5") || [];

    shellRows.forEach((row, index) => {
      convertField(`DataGridView5.${index}.Diameter`, (v) => v / 25.4);
      convertField(`DataGridView5.${index}.Can Length`, (v) => v / 25.4);
      convertField(`DataGridView5.${index}.Provided Thk`, (v) => v / 25.4);
      // kg -> lb
      convertField(`DataGridView5.${index}.Weight`, (v) => v * 2.2046226218);
    });

    // NOZZLES - DataGridView1
    const nozzleRows = getValues("DataGridView1") || [];

    nozzleRows.forEach((row, index) => {
      nozzleLengthFields.forEach((fieldName) => {
        convertField(`DataGridView1.${index}.${fieldName}`, (v) => v / 25.4);
      });
    });

    // NOZZLE GEOMETRY - DataGridView10
    const nozzleGeometryRows = getValues("DataGridView10") || [];

    nozzleGeometryRows.forEach((row, index) => {
      convertField(`DataGridView10.${index}.Dist From DL`, (v) => v / 25.4);
      convertField(
        `DataGridView10.${index}.Dist From CL (+/-)`,
        (v) => v / 25.4
      );
    });

    // ATTACHMENTS / SADDLE
    saddleLengthFields.forEach((fieldName) => {
      convertField(fieldName, (v) => v / 25.4);
    });

    saddleStressFields.forEach((fieldName) => {
      convertField(fieldName, (v) => v * 145.0377377);
    });
    // Load on one saddle: kg -> lb
    convertField("txt_Q_SC", (v) => v * 2.2046226218);
    // Allowable Stress: N/mm² -> psi
    convertField("txt_AS_SC", (v) => v * 145.0377377);
    // Angles stay degrees: TxtBx_Wb_CntctAngl, txt_CA_SC
  };

  const convertEnglishToSI = () => {
    // Pressure: psi -> N/mm²
    convertField("designInternalPressure", (v) => v / 145.0377377);
    convertField("designExternalPressure", (v) => v / 145.0377377);
    convertField("hydrotestPressure", (v) => v / 145.0377377);

    // Temperature: °F -> °C
    convertField("designTemperature", (v) => ((v - 32) * 5) / 9);
    convertField("mdmt", (v) => ((v - 32) * 5) / 9);

    // Length: in -> mm
    convertField("corrosionAllowanceShell", (v) => v * 25.4);
    convertField("vesselDiameter", (v) => v * 25.4);
    convertField("vesselLengthTT", (v) => v * 25.4);
    convertField("vesselLengthSS", (v) => v * 25.4);
    convertField("insulationThk", (v) => v * 25.4);

    // Height: ft -> m
    convertField("liquidHeight", (v) => v / 3.280839895);

    // Density: lb/ft³ -> kg/m³
    convertField("liquidDensity", (v) => v / 0.0624279606);
    convertField("insulationDensity", (v) => v / 0.0624279606);

    // Wind speed: mph -> km/h
    convertField("windSpeed", (v) => v / 0.6213711922);

    // SHELL - DataGridView5
    const shellRows = getValues("DataGridView5") || [];

    shellRows.forEach((row, index) => {
      convertField(`DataGridView5.${index}.Diameter`, (v) => v * 25.4);
      convertField(`DataGridView5.${index}.Can Length`, (v) => v * 25.4);
      convertField(`DataGridView5.${index}.Provided Thk`, (v) => v * 25.4);
      // lb -> kg
      convertField(`DataGridView5.${index}.Weight`, (v) => v / 2.2046226218);
    });

    // NOZZLES - DataGridView1
    const nozzleRows = getValues("DataGridView1") || [];

    nozzleRows.forEach((row, index) => {
      nozzleLengthFields.forEach((fieldName) => {
        convertField(`DataGridView1.${index}.${fieldName}`, (v) => v * 25.4);
      });
    });

    // NOZZLE GEOMETRY - DataGridView10
    const nozzleGeometryRows = getValues("DataGridView10") || [];

    nozzleGeometryRows.forEach((row, index) => {
      convertField(`DataGridView10.${index}.Dist From DL`, (v) => v * 25.4);
      convertField(
        `DataGridView10.${index}.Dist From CL (+/-)`,
        (v) => v * 25.4
      );
    });

    // ATTACHMENTS / SADDLE
    saddleLengthFields.forEach((fieldName) => {
      convertField(fieldName, (v) => v * 25.4);
    });
    saddleStressFields.forEach((fieldName) => {
      convertField(fieldName, (v) => v / 145.0377377);
    });
    // Load on one saddle: lb -> kg
    convertField("txt_Q_SC", (v) => v / 2.2046226218);
    // Allowable Stress: psi -> N/mm²
    convertField("txt_AS_SC", (v) => v / 145.0377377);
    // Angles stay degrees: TxtBx_Wb_CntctAngl, txt_CA_SC
  };

  const selectSIUnits = () => {
    if (unitsLocked) return;
    if (isSI) return;
    if (isEnglish) {
      convertEnglishToSI();
    }

    setValue("siUnits", true, { shouldDirty: true });
    setValue("engUnits", false, { shouldDirty: true });
  };

  const selectEnglishUnits = () => {
    if (unitsLocked) return;
    if (isEnglish) return;

    if (isSI) {
      convertSIToEnglish();
    }

    setValue("siUnits", false, { shouldDirty: true });
    setValue("engUnits", true, { shouldDirty: true });
  };

  return (
    <Grid item xs={2} sm={2.6} md={3} lg={3} xl={2.6}>
      <Box {...designTabBoxProps} noValidate>
        {designDetails.map((elem) => (
          <Grid id="inner-container" item container columns={12}>
            <Grid item sx={{ ...GridStyle }} xs={6}>
              <Typography fontSize={"0.75rem"}>{elem.label}</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              data-edge-mentor={elem?.disabled ? elem.name : undefined}
              sx={{
                ...GridStyle,

                // Disabled/read-only form controls do not reliably emit
                // pointer events from the native input. For disabled
                // Design fields, let the click land on this wrapper so the
                // existing PV-wide EdgeMentorHost can still detect the field.
                ...(elem?.disabled
                  ? {
                      "& .MuiFormControl-root": {
                        pointerEvents: "none",
                      },
                    }
                  : {}),
              }}
            >
              {elem.select ? (
                <CustomSelect
                  label=""
                  name={elem.name}
                  required
                  menuItems={elem.select}
                />
              ) : (
                <CustomTextField
                  label=""
                  id={elem.label}
                  name={elem.name}
                  disabled={elem?.disabled || false}
                  inputProps={elem?.disabled ? { style: greyedOutFields } : {}}
                />
              )}
            </Grid>
          </Grid>
        ))}
        {
          <FormControl error={Boolean(errors.siUnits)}>
            <FormGroup row>
              <FormLabel
                sx={{ fontSize: "0.75rem", mt: 0.6, color: "var(--app-text)" }}
                id="units"
              >
                Units
              </FormLabel>
              <FormControlLabel
                data-edge-mentor="siUnits"
                control={
                  <Controller
                    name="siUnits"
                    control={control}
                    defaultValue={true}
                    render={({ field: { value, ref, ...field } }) => (
                      <Checkbox
                        row
                        {...field}
                        inputRef={ref}
                        disabled={unitsLocked}
                        sx={{
                          "& .MuiSvgIcon-root": { fontSize: "0.75rem", ml: 1 },
                        }}
                        onChange={(e) => {
                          if (e.target.checked && !unitsLocked) {
                            selectSIUnits();
                          }
                        }}
                        checked={!!value}
                      />
                    )}
                  />
                }
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.75rem" } }}
                label="SI Units"
              />
              <FormControlLabel
                data-edge-mentor="engUnits"
                control={
                  <Controller
                    name="engUnits"
                    control={control}
                    defaultValue={false}
                    render={({ field: { value, ref, ...field } }) => (
                      <Checkbox
                        {...field}
                        row
                        inputRef={ref}
                        disabled={unitsLocked}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "0.75rem" } }}
                        onChange={(e) => {
                          if (e.target.checked && !unitsLocked) {
                            selectEnglishUnits();
                          }
                        }}
                        checked={!!value}
                      />
                    )}
                  />
                }
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.75rem" } }}
                label="English Units"
              />
            </FormGroup>
          </FormControl>
        }
      </Box>
    </Grid>
  );
};

export default DesignDetailsGrid;
